import { Box, Typography } from "@mui/material";
import plus from "../../../../assets/images/images-svg/plus.svg";
import { ReactComponent as Align } from "../../../../assets/images/images-svg/align-justify.svg";
import React, { useEffect, useState } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { automation, boardAutomation } from "../../../../types/board";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useAppDispatch } from "../../../../redux/store";
import { setAutomationOrder } from "../../../../redux/features/custom-board/custom-board-slice";

interface IBoardTitle {
  board: boardAutomation;
  setRule: (boardId: string, index: number) => void;
  selectedRule: { id: string; index: number };
  addRule: () => void;
}

// Draggable Automation Item Component
const DraggableAutomationItem: React.FC<{
  automation: automation;
  index: number;
  boardId: string;
  setRule: (boardId: string, index: number) => void;
  selectedRule: { id: string; index: number };
}> = ({ automation, index, boardId, setRule, selectedRule }) => {
  return (
    <Draggable draggableId={`automation-${index}`} index={index}>
      {(provided, snapshot) => (
        <li
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={() => setRule(boardId, index)}
          className={
            selectedRule.id === boardId && selectedRule.index === index
              ? "automationSidebar__innerItem-selected"
              : "automationSidebar__innerItem-title"
          }
        >
          <>
            <div className="icons-Container ruleStatus">
              <div {...provided.dragHandleProps}>
                <Align
                  className="icon"
                  style={{
                    cursor: "grab",
                    pointerEvents: "none",
                  }}
                />
              </div>
              <div
                className={
                  automation.onHold ? "onHold" : automation.apply ? "active" : "off"
                }
              />
            </div>

            <Typography
              style={{
                userSelect: "none",
                pointerEvents: "none",
              }}
            >
              {automation.name ? automation.name : `Rule ${index + 1}`}
            </Typography>
          </>
        </li>
      )}
    </Draggable>
  );
};

// Droppable container for automation list
const DroppableAutomationList: React.FC<{
  automations: automation[];
  boardId: string;
  setRule: (boardId: string, index: number) => void;
  selectedRule: { id: string; index: number };
  onDragEnd: (result: DropResult) => void;
}> = ({ automations, boardId, setRule, selectedRule }) => {
  return (
    <Droppable droppableId={`automation-list-${boardId}`}>
      {(provided) => (
        <ul
          ref={provided.innerRef}
          {...provided.droppableProps}
          className="automationSidebar__innerItem"
        >
          {automations.length > 0 &&
            automations.map((automation, index) => (
              <DraggableAutomationItem
                key={`automation-${index}`}
                automation={automation}
                index={index}
                boardId={boardId}
                setRule={setRule}
                selectedRule={selectedRule}
              />
            ))}
          {provided.placeholder}
        </ul>
      )}
    </Droppable>
  );
};

const BoardTitle: React.FC<IBoardTitle> = ({
  board,
  setRule,
  selectedRule,
  addRule,
}) => {
  const dispatch = useAppDispatch();
  const [show, setShow] = useState(false);
  const [automations, setAutomations] = useState<any[]>([]);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) return;

    // Perform the reordering
    const newAutomations = [...automations];
    const [dragItem] = newAutomations.splice(sourceIndex, 1);
    newAutomations.splice(destinationIndex, 0, dragItem);

    // Update order property
    const updatedAutomations = newAutomations.map((automation, index) => ({
      ...automation,
      order: index,
    }));

    setAutomations(updatedAutomations);

    // Dispatch the final order to Redux
    dispatch(
      setAutomationOrder({
        id: board.id,
        automation: updatedAutomations,
      })
    );
  };

  // update automations
  useEffect(() => {
    setAutomations(board.automation);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.automation.length]);

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box className="automationSidebar__item">
        <Typography className="automationSidebar__item-title">
          <span
            style={{
              display: "flex",
              alignItems: "flex-start",
              cursor: "pointer",
            }}
            onClick={() => {
              setShow(!show);
              setRule(board.id, 0);
            }}
          >
            {!show ? <ArrowForwardIcon /> : <ArrowDownwardIcon />}{" "}
            {board.boardName}
          </span>
          <span
          >
            <img
              alt="added icon"
              src={plus}
              onClick={() => {
                setShow(true);
                addRule();
              }}
              className="mainHeader__logo"
              style={{ width: "12px", height: "12px" }}
            />
          </span>
        </Typography>

        {show && (
        <DroppableAutomationList
          automations={automations}
          boardId={board.id}
          setRule={setRule}
          selectedRule={selectedRule}
          onDragEnd={handleDragEnd}
        />
        )} 
      </Box>
    </DragDropContext>
  );
};

export default BoardTitle;
